import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface VimeoCreateResult {
  vimeo_id: string;
  vimeo_uri: string;
  upload_link: string;
}

interface VimeoFinalizeResult {
  thumbnail_url: string | null;
  player_url: string;
  duration: string;
  duration_seconds: number;
  name: string;
  status: string;
}

interface VimeoInfoResult {
  vimeo_id: string;
  vimeo_uri: string;
  thumbnail_url: string | null;
  player_url: string;
  duration: string;
  duration_seconds: number;
  name: string;
  description: string;
}

interface VimeoVerifyResult {
  connected: boolean;
  account_name: string;
  account_type: string;
  upload_quota: any;
}

async function invokeVimeo<T>(action: string, payload: Record<string, any> = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke('vimeo-upload', {
    body: { action, ...payload },
  });
  if (error) throw new Error(error.message || 'Vimeo function error');
  if (data?.error) throw new Error(data.error);
  return data as T;
}

/** Upload a video file to Vimeo using tus resumable protocol */
export function useVimeoUpload() {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(async (
    file: File,
    name: string,
    description?: string
  ): Promise<{ vimeo_id: string; vimeo_uri: string; thumbnail_url: string | null; player_url: string; duration: string }> => {
    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      const createResult = await invokeVimeo<VimeoCreateResult>('create', {
        name,
        size: file.size,
        description: description || '',
      });

      const { upload_link, vimeo_id, vimeo_uri } = createResult;

      const CHUNK_SIZE = 64 * 1024 * 1024;
      let offset = 0;

      while (offset < file.size) {
        const chunk = file.slice(offset, offset + CHUNK_SIZE);
        const res = await fetch(upload_link, {
          method: 'PATCH',
          headers: {
            'Tus-Resumable': '1.0.0',
            'Upload-Offset': String(offset),
            'Content-Type': 'application/offset+octet-stream',
          },
          body: chunk,
        });

        if (!res.ok && res.status !== 204) {
          throw new Error(`Upload failed at offset ${offset}: ${res.status}`);
        }

        const newOffset = res.headers.get('Upload-Offset');
        offset = newOffset ? parseInt(newOffset) : offset + chunk.size;
        setProgress(Math.round((offset / file.size) * 100));
      }

      await new Promise(r => setTimeout(r, 2000));

      const finalResult = await invokeVimeo<VimeoFinalizeResult>('finalize', {
        vimeo_uri,
      });

      setProgress(100);
      setUploading(false);

      return {
        vimeo_id,
        vimeo_uri,
        thumbnail_url: finalResult.thumbnail_url,
        player_url: finalResult.player_url,
        duration: finalResult.duration,
      };
    } catch (e: any) {
      setError(e.message);
      setUploading(false);
      throw e;
    }
  }, []);

  return { upload, uploading, progress, error };
}

/** Get info for an existing Vimeo video URL */
export async function getVimeoInfo(vimeoUrl: string): Promise<VimeoInfoResult> {
  return invokeVimeo<VimeoInfoResult>('get-info', { vimeo_url: vimeoUrl });
}

/** Verify Vimeo API connection */
export async function verifyVimeoConnection(): Promise<VimeoVerifyResult> {
  return invokeVimeo<VimeoVerifyResult>('verify-token');
}

export interface SavedVideoProgress {
  last_position: number;
  progress_percent: number;
  completed: boolean;
  completed_at: string | null;
}

/** Save video playback position — preserves completed state on rewatches */
export async function saveVideoPosition(
  contentId: string,
  position: number,
  progressPercent: number,
): Promise<SavedVideoProgress | null> {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) throw userError;
  if (!user) return null;

  const safePosition = Math.max(0, Math.floor(Number.isFinite(position) ? position : 0));
  const raw = Number.isFinite(progressPercent) ? progressPercent : 0;
  const effectivePercent = Math.min(100, Math.max(raw, safePosition > 0 ? 1 : 0));

  const { data: existing, error: existErr } = await supabase
    .from('content_progress')
    .select('completed, completed_at')
    .eq('user_id', user.id)
    .eq('content_id', contentId)
    .maybeSingle();
  if (existErr) throw existErr;

  const alreadyCompleted = existing?.completed === true;
  const isNowComplete = effectivePercent >= 90;
  const finalCompleted = alreadyCompleted || isNowComplete;

  const { data, error } = await supabase
    .from('content_progress')
    .upsert(
      {
        user_id: user.id,
        content_id: contentId,
        last_position: finalCompleted ? 0 : safePosition,
        progress_percent: Math.round(effectivePercent),
        completed: finalCompleted,
        completed_at: finalCompleted
          ? existing?.completed_at || new Date().toISOString()
          : null,
      } as any,
      { onConflict: 'user_id,content_id' },
    )
    .select('last_position, progress_percent, completed, completed_at')
    .single();

  if (error) {
    console.error('Error saving video position:', error);
    throw error;
  }
  return data as SavedVideoProgress;
}

/** Get saved video position */
export async function getVideoPosition(contentId: string): Promise<SavedVideoProgress | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data } = await supabase
    .from('content_progress')
    .select('last_position, progress_percent, completed, completed_at')
    .eq('user_id', user.id)
    .eq('content_id', contentId)
    .maybeSingle();

  return data as SavedVideoProgress | null;
}
